import { observer } from 'mobx-react-lite';
import React from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../colorVars';
import { modifySoftOrder } from '../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/modifySoftOrders';
import { updatePlugins } from '../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/updatePlugins';
import { useWebsocketStore } from '../../../../../../../../stores/websocketStore/websocketProvider';
import { EditableTableField } from '../../../../../../../atomic/inputs/editableTableField/EditableTableField';
import { TableSelect } from '../../../../../../../atomic/inputs/tableSelect/TableSelect';
import { Body3 } from '../../../../../../../atomic/text/Text';
import { IndividualPanelHeader } from '../IndividualPanelHeader';
import style from '../IndividualTacticStyle.module.css';

export const RatchetBracketTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId];
    const tactic = tradePlan.exitTactics['RATCHET_BRACKET'];
    const tacticId = tactic?.tacticId ?? null;
    const qtyBasis = tactic?.qtyBasis ?? null;
    const mode = tactic?.mode ?? 'EQUAL_INTERVAL';
    const isOcoMode = mode === 'OCO_ANCHORED';
    const inPosition = tradePlan?.inPosition ?? null;
    const softOrder = tradePlanStore.softOrdersById[tacticId] ?? null;
    const pendingTargets = softOrder?.value?.tacticValue?.pendingTargets ?? [];

    const updateSoftOrder = (type, value) => {
      modifySoftOrder({ softOrder, type, value });
    };
    const updateTactic = (field, newValue) => {
      updatePlugins(
        'tactic',
        { [field]: newValue },
        'RATCHET_BRACKET',
        tradePlanId,
        null
      );
    };

    if (!tactic) return null;

    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <IndividualPanelHeader />
            {inPosition && softOrder ? (
              // In-position view: show the live stop and the currently pending targets.
              <tbody>
                <tr>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      Stop
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                    <EditableTableField
                      inputType="number"
                      bodySize={3}
                      initialValue={softOrder.value.tacticValue.stop}
                      updateValue={(newValue) =>
                        updateSoftOrder('stop', newValue)
                      }
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                  </td>
                  <td></td>
                  <td></td>
                </tr>
                {pendingTargets.map((target, index) => {
                  const targetPrice = target.targetPrice ?? target.price;
                  return (
                    <tr key={`${targetPrice}-${index}`}>
                      <td className={style.labelTd}>
                        <Body3 fontWeight={700} color={colors.lightGrey}>
                          Target {index + 1}
                        </Body3>
                      </td>
                      <td className={style.inputTd}>
                        <Body3
                          fontWeight={700}
                          color={colors.lightGrey}
                          margin="3px 3px 0 0"
                        >
                          $
                        </Body3>
                        <Body3 fontWeight={700} color={colors.lightGrey}>
                          {targetPrice}
                        </Body3>
                      </td>
                      <td className={style.labelTd}>
                        <Body3 fontWeight={700} color={colors.lightGrey}>
                          Qty.
                        </Body3>
                      </td>
                      <td className={style.inputTd}>
                        <Body3 fontWeight={700} color={colors.lightGrey}>
                          {target.qty}
                        </Body3>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ) : isOcoMode ? (
              // OCO_ANCHORED not-in-position view: show OCO prices (read-only) + editable qty fields
              <tbody>
                <tr>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      Stop Price
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {tactic.ocoStopPrice}
                    </Body3>
                  </td>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      Target Price
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {tactic.ocoTargetPrice}
                    </Body3>
                  </td>
                </tr>
                <tr>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      Num. of Targets
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {tactic.ocoNumberOfTargets}
                    </Body3>
                  </td>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      Qty. Basis
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <TableSelect
                      margin="4px 1px 0 0"
                      color={colors.lightGrey}
                      backgroundColor={colors.black}
                      value={qtyBasis}
                      onChange={(newValue) =>
                        updateTactic('qtyBasis', newValue)
                      }
                      options={[
                        { label: 'Quantity', value: 'QUANTITY' },
                        { label: 'Percentage', value: 'PERCENT' },
                      ]}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {qtyBasis === 'PERCENT' ? '% per Bracket' : 'Qty. per Bracket'}
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    {qtyBasis !== 'PERCENT' && (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        #
                      </Body3>
                    )}
                    <EditableTableField
                      inputType="number"
                      bodySize={3}
                      initialValue={
                        qtyBasis === 'PERCENT'
                          ? Number(tactic.qtyBasisValuePerBracket) * 100
                          : tactic.qtyBasisValuePerBracket
                      }
                      updateValue={(newValue) =>
                        updateTactic(
                          'qtyBasisValuePerBracket',
                          qtyBasis === 'PERCENT'
                            ? Number(newValue) / 100
                            : newValue
                        )
                      }
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                    {qtyBasis === 'PERCENT' && (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        %
                      </Body3>
                    )}
                  </td>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {qtyBasis === 'QUANTITY' ? 'Trail Qty.' : 'Trail %'}
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    {qtyBasis === 'QUANTITY' && (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        #
                      </Body3>
                    )}
                    <EditableTableField
                      inputType="number"
                      bodySize={3}
                      initialValue={
                        qtyBasis !== 'QUANTITY'
                          ? Number(tactic.trailQtyBasisValue) * 100
                          : tactic.trailQtyBasisValue
                      }
                      updateValue={(newValue) =>
                        updateTactic(
                          'trailQtyBasisValue',
                          qtyBasis !== 'QUANTITY'
                            ? Number(newValue) / 100
                            : newValue
                        )
                      }
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                    {qtyBasis !== 'QUANTITY' && (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        %
                      </Body3>
                    )}
                  </td>
                </tr>
              </tbody>
            ) : (
              // EQUAL_INTERVAL not-in-position view: existing behavior
              <tbody>
                <tr>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      Qty. Basis
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <TableSelect
                      margin="4px 1px 0 0"
                      color={colors.lightGrey}
                      backgroundColor={colors.black}
                      value={qtyBasis}
                      onChange={(newValue) =>
                        updateTactic('qtyBasis', newValue)
                      }
                      options={[
                        { label: 'Profit', value: 'PROFIT' },
                        { label: 'Quantity', value: 'QUANTITY' },
                        { label: 'Percentage', value: 'PERCENT' },
                      ]}
                    />
                  </td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {qtyBasis === 'PROFIT'
                        ? 'Target Profit'
                        : 'Target per Bracket'}
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                    <EditableTableField
                      inputType="number"
                      bodySize={3}
                      initialValue={tactic.targetPerBracket}
                      updateValue={(newValue) =>
                        updateTactic('targetPerBracket', newValue)
                      }
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                  </td>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {qtyBasis === 'PROFIT' ? 'Stop Loss' : 'Stop per Bracket'}
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                    <EditableTableField
                      inputType="number"
                      bodySize={3}
                      initialValue={tactic.stopPerBracket}
                      updateValue={(newValue) =>
                        updateTactic('stopPerBracket', newValue)
                      }
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                  </td>
                </tr>

                <tr>
                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {qtyBasis === 'PROFIT'
                        ? '# of Brackets'
                        : qtyBasis === 'PERCENT'
                          ? '% per Bracket'
                          : 'Qty. Per Bracket'}
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    {qtyBasis !== 'PERCENT' ? (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        {qtyBasis === 'QUANTITY' ? '#' : '$'}
                      </Body3>
                    ) : null}
                    <EditableTableField
                      inputType="number"
                      bodySize={3}
                      initialValue={
                        qtyBasis === 'PERCENT'
                          ? Number(tactic.qtyBasisValuePerBracket) * 100
                          : tactic.qtyBasisValuePerBracket
                      }
                      updateValue={(newValue) =>
                        updateTactic(
                          'qtyBasisValuePerBracket',
                          qtyBasis === 'PERCENT'
                            ? Number(newValue) / 100
                            : newValue
                        )
                      }
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                    {qtyBasis === 'PERCENT' ? (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        %
                      </Body3>
                    ) : null}
                  </td>

                  <td className={style.labelTd}>
                    <Body3 fontWeight={700} color={colors.lightGrey}>
                      {qtyBasis === 'QUANTITY' ? 'Trail Qty.' : 'Trail %'}
                    </Body3>
                  </td>
                  <td className={style.inputTd}>
                    {qtyBasis === 'QUANTITY' ? (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        #
                      </Body3>
                    ) : null}
                    <EditableTableField
                      inputType="number"
                      bodySize={3}
                      initialValue={
                        qtyBasis !== 'QUANTITY'
                          ? Number(tactic.trailQtyBasisValue) * 100
                          : tactic.trailQtyBasisValue
                      }
                      updateValue={(newValue) =>
                        updateTactic(
                          'trailQtyBasisValue',
                          qtyBasis !== 'QUANTITY'
                            ? Number(newValue) / 100
                            : newValue
                        )
                      }
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                    />
                    {qtyBasis !== 'QUANTITY' ? (
                      <Body3
                        fontWeight={700}
                        color={colors.lightGrey}
                        margin="3px 3px 0 0"
                      >
                        %
                      </Body3>
                    ) : null}
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </TabPanel>
    );
  }
);

RatchetBracketTacticPanel.tabsRole = 'TabPanel';
